/**
 * Settings page per COMP-007 Story 11.
 * Admin-only: configure portal name, timezone, session defaults,
 * notification preferences, support URL.
 */

import { useState, useEffect, useMemo } from "react";
import { Settings, Save, Shield, ChevronDown, ChevronRight, Ticket } from "lucide-react";
import { toast } from "sonner";
import { TECHNICIAN_TIERS, TIER_ROLES, ENTRA_ROLE_DISPLAY_NAMES, TEAMS_SPECIALIST_DEFAULT_ROLES } from "@gdapwif/shared";
import { GlassCard } from "@/components/sc/glass-card";
import { TierGroupPicker } from "@/components/sc/tier-group-picker";
import {
  useSettings,
  useUpdateSettings,
  useNotificationSettings,
  useUpdateHaloPsaTicketType,
  useUpdateHaloPsaPriorities,
} from "@/hooks/use-settings";
import type { PortalSettings } from "@/types";

const TIMEZONE_OPTIONS = [
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Australia/Perth",
  "Australia/Adelaide",
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
  "Europe/London",
  "Asia/Tokyo",
  "Asia/Singapore",
];

const NOTIFICATION_TYPES = [
  { key: "gdapApproval", label: "GDAP Approval Requests" },
  { key: "gdapExpiry", label: "GDAP Expiry Warnings" },
  { key: "pimSlaBreach", label: "PIM SLA Breach Alerts" },
  { key: "systemHealth", label: "System Health Changes" },
  { key: "cacheRefresh", label: "Cache Refresh Events" },
];

const GDAP_TIER_GROUP_FIELDS = [
  { key: "gdap_tier_0_group_id" as const, nameKey: "gdap_tier_0_group_name" as const, label: "GDAP-Tier-0", description: "Emergency / break-glass access" },
  { key: "gdap_tier_1_group_id" as const, nameKey: "gdap_tier_1_group_name" as const, label: "GDAP-Tier-1", description: "Senior technician access" },
  { key: "gdap_tier_2_group_id" as const, nameKey: "gdap_tier_2_group_name" as const, label: "GDAP-Tier-2", description: "Standard technician access" },
  { key: "gdap_tier_3_group_id" as const, nameKey: "gdap_tier_3_group_name" as const, label: "GDAP-Tier-3", description: "Junior / limited access" },
  { key: "gdap_tier_4_group_id" as const, nameKey: "gdap_tier_4_group_name" as const, label: "GDAP-Tier-4", description: "Read-only / monitoring" },
  { key: "gdap_teams_specialist_group_id" as const, nameKey: "gdap_teams_specialist_group_name" as const, label: "GDAP-TeamsSpecialist", description: "Teams Specialist — configurable role set" },
];

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidGuidOrEmpty(value: string): boolean {
  if (!value) return true;
  return GUID_REGEX.test(value.trim());
}

// DV-007: HTTPS URL validation
function isValidHttpsUrl(value: string): boolean {
  if (!value) return true; // empty is allowed
  try {
    const url = new URL(value);
    return url.protocol === "https:";
  } catch {
    return false;
  }
}

export function SettingsPage() {
  const settingsQuery = useSettings();
  const updateMutation = useUpdateSettings();

  const [form, setForm] = useState<Partial<PortalSettings>>({});
  const [urlError, setUrlError] = useState("");
  const [guidErrors, setGuidErrors] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState(false);

  // Initialize form when settings load
  useEffect(() => {
    if (settingsQuery.data) {
      setForm(settingsQuery.data);
      setDirty(false);
    }
  }, [settingsQuery.data]);

  function updateField<K extends keyof PortalSettings>(
    key: K,
    value: PortalSettings[K],
  ) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);

    if (key === "supportUrl") {
      setUrlError(
        isValidHttpsUrl(value as string) ? "" : "Must be a valid HTTPS URL",
      );
    }

    if (typeof key === "string" && key.endsWith("_group_id") && (key.startsWith("gdap_tier_") || key.startsWith("gdap_teams_"))) {
      setGuidErrors((prev) => ({
        ...prev,
        [key]: isValidGuidOrEmpty(value as string) ? "" : "Must be a valid GUID (e.g. a1b2c3d4-e5f6-7890-abcd-ef1234567890)",
      }));
    }
  }

  const hasGuidErrors = useMemo(
    () => Object.values(guidErrors).some((e) => e !== ""),
    [guidErrors],
  );

  function handleSave() {
    if (urlError || hasGuidErrors) return;

    updateMutation.mutate(form, {
      onSuccess: () => {
        toast.success("Settings saved");
        setDirty(false);
      },
      onError: (err) => {
        if (err instanceof Error && err.message.includes("403")) {
          toast.error("You don't have permission to modify settings.");
        } else {
          toast.error("Failed to save settings. Try again.");
        }
      },
    });
  }

  if (settingsQuery.isLoading) {
    return (
      <GlassCard className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
          <p className="text-body-sm text-sc-text-muted">Loading settings...</p>
        </div>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* General */}
      <GlassCard>
        <div className="flex items-center gap-2 mb-4">
          <Settings size={18} className="text-sc-primary" />
          <h3 className="text-h3 font-display text-sc-navy">General</h3>
        </div>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
              Portal Name
            </label>
            <input
              type="text"
              value={form.portalName ?? ""}
              onChange={(e) => updateField("portalName", e.target.value)}
              className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
            />
          </div>
          <div>
            <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
              Default Timezone
            </label>
            <select
              value={form.defaultTimezone ?? "Australia/Sydney"}
              onChange={(e) => updateField("defaultTimezone", e.target.value)}
              className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </div>
        </div>
      </GlassCard>

      {/* Security */}
      <GlassCard>
        <h3 className="text-h3 font-display text-sc-navy mb-4">Security</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
              Default Session Duration (hours)
            </label>
            <input
              type="number"
              min={1}
              max={8}
              value={form.defaultSessionDuration ?? 1}
              onChange={(e) =>
                updateField(
                  "defaultSessionDuration",
                  Math.min(8, Math.max(1, Number(e.target.value))),
                )
              }
              className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
            />
            <p className="text-label text-sc-text-muted mt-1">
              PIM session default. Min 1, max 8 hours.
            </p>
          </div>
          <div className="p-3 rounded-card bg-sc-surface-hover border border-sc-border">
            <span className="text-label text-sc-text-muted uppercase tracking-widest">
              Revocation SLA
            </span>
            <p className="text-body text-sc-text font-medium mt-1">
              15 minutes
            </p>
            <p className="text-label text-sc-text-muted mt-0.5">
              Maximum time to revoke an active PIM session.
            </p>
          </div>
        </div>
      </GlassCard>

      {/* GDAP Configuration */}
      <GlassCard className="relative z-10 overflow-visible">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={18} className="text-sc-primary" />
          <h3 className="text-h3 font-display text-sc-navy">
            GDAP Configuration
          </h3>
        </div>
        <p className="text-body-sm text-sc-text-muted mb-4">
          Search for existing Entra ID security groups or create new
          role-assignable groups for each tier.
        </p>
        <div className="space-y-4 max-w-lg overflow-visible">
          {GDAP_TIER_GROUP_FIELDS.map((field) => (
            <TierGroupPicker
              key={field.key}
              tier={field.key === "gdap_teams_specialist_group_id" ? "TeamsSpecialist" : field.key.replace("gdap_tier_", "Tier-").replace("_group_id", "")}
              tierLabel={field.label}
              description={field.description}
              value={form[field.key] ?? ""}
              savedName={form[field.nameKey] ?? ""}
              onChange={(groupId, groupName) => {
                updateField(field.key, groupId);
                updateField(field.nameKey, groupName ?? "");
              }}
            />
          ))}
        </div>
      </GlassCard>

      {/* GDAP Roles per Tier (read-only reference) */}
      <GlassCard>
        <div className="flex items-center gap-2 mb-4">
          <Shield size={18} className="text-sc-primary" />
          <h3 className="text-h3 font-display text-sc-navy">
            GDAP Roles per Tier
          </h3>
        </div>
        <p className="text-body-sm text-sc-text-muted mb-4">
          Entra ID roles assigned to each technician tier for GDAP relationships. These are configured in code and apply to all OPEN customers.
        </p>
        <div className="space-y-3">
          {TECHNICIAN_TIERS.map((tier) => (
            <TierRoleCard key={tier} tier={tier} />
          ))}
        </div>
      </GlassCard>

      {/* Teams Specialist Roles (read-only reference) */}
      <TeamsSpecialistRoleCard />

      {/* Notifications */}
      <GlassCard>
        <h3 className="text-h3 font-display text-sc-navy mb-4">
          Notifications
        </h3>
        <div className="space-y-3 max-w-md">
          {NOTIFICATION_TYPES.map((nt) => (
            <label
              key={nt.key}
              className="flex items-center gap-3 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={form.notificationPreferences?.[nt.key] ?? true}
                onChange={(e) => {
                  const prefs = {
                    ...(form.notificationPreferences ?? {}),
                    [nt.key]: e.target.checked,
                  };
                  updateField("notificationPreferences", prefs);
                }}
                className="h-4 w-4 rounded border-sc-border text-sc-primary focus:ring-sc-primary"
              />
              <span className="text-body-sm text-sc-text">{nt.label}</span>
            </label>
          ))}
        </div>
      </GlassCard>

      {/* HaloPSA Integration */}
      <HaloPsaSettingsCard />

      {/* Support */}
      <GlassCard>
        <h3 className="text-h3 font-display text-sc-navy mb-4">Support</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
              Support URL
            </label>
            <input
              type="url"
              value={form.supportUrl ?? ""}
              onChange={(e) => updateField("supportUrl", e.target.value)}
              onBlur={() => {
                if (form.supportUrl && !isValidHttpsUrl(form.supportUrl)) {
                  setUrlError("Must be a valid HTTPS URL");
                }
              }}
              placeholder="https://support.example.com"
              className={`h-9 w-full rounded-input border px-3 text-body-sm text-sc-text bg-sc-input-bg ${
                urlError
                  ? "border-sc-danger"
                  : "border-sc-border"
              }`}
            />
            {urlError && (
              <p className="text-label text-sc-danger mt-1">{urlError}</p>
            )}
          </div>
        </div>
      </GlassCard>

      {/* Save button — sticky so it's always accessible */}
      {dirty && (
        <div className="sticky bottom-4 z-40 flex justify-end">
          <button
            onClick={handleSave}
            disabled={!dirty || !!urlError || hasGuidErrors || updateMutation.isPending}
            className="flex items-center gap-1 px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 disabled:opacity-50 transition-colors shadow-lg"
          >
            <Save size={16} />
            {updateMutation.isPending ? "Saving..." : "Save Settings"}
          </button>
        </div>
      )}

      {/* Last updated info */}
      {settingsQuery.data?.updatedAt && (
        <p className="text-label text-sc-text-muted text-right">
          Last updated{" "}
          {new Date(settingsQuery.data.updatedAt).toLocaleString()} by{" "}
          {settingsQuery.data.updatedBy}
        </p>
      )}
    </div>
  );
}

/** HaloPSA integration settings — ticket type + priority mapping. */
function HaloPsaSettingsCard() {
  const notifSettings = useNotificationSettings();
  const updateTicketType = useUpdateHaloPsaTicketType();
  const updatePriorities = useUpdateHaloPsaPriorities();

  const [ticketTypeId, setTicketTypeId] = useState("");
  const [priorityDefault, setPriorityDefault] = useState("");
  const [priorityCritical, setPriorityCritical] = useState("");
  const [haloDirty, setHaloDirty] = useState(false);

  useEffect(() => {
    if (notifSettings.data) {
      setTicketTypeId(notifSettings.data.tickettypeId?.toString() ?? "");
      setPriorityDefault(notifSettings.data.priorityDefault?.toString() ?? "2");
      setPriorityCritical(notifSettings.data.priorityCritical?.toString() ?? "1");
      setHaloDirty(false);
    }
  }, [notifSettings.data]);

  const ticketTypeValid = ticketTypeId === "" || (Number(ticketTypeId) > 0 && Number.isInteger(Number(ticketTypeId)));
  const prioritiesValid =
    Number(priorityDefault) > 0 && Number.isInteger(Number(priorityDefault)) &&
    Number(priorityCritical) > 0 && Number.isInteger(Number(priorityCritical));

  function handleSaveHalo() {
    if (!ticketTypeValid || !prioritiesValid) return;

    const promises: Promise<unknown>[] = [];

    if (ticketTypeId && Number(ticketTypeId) !== notifSettings.data?.tickettypeId) {
      promises.push(
        new Promise((resolve, reject) =>
          updateTicketType.mutate(Number(ticketTypeId), { onSuccess: resolve, onError: reject }),
        ),
      );
    }

    if (
      Number(priorityDefault) !== notifSettings.data?.priorityDefault ||
      Number(priorityCritical) !== notifSettings.data?.priorityCritical
    ) {
      promises.push(
        new Promise((resolve, reject) =>
          updatePriorities.mutate(
            { default: Number(priorityDefault), critical: Number(priorityCritical) },
            { onSuccess: resolve, onError: reject },
          ),
        ),
      );
    }

    if (promises.length === 0) {
      setHaloDirty(false);
      return;
    }

    Promise.all(promises)
      .then(() => {
        toast.success("HaloPSA settings saved");
        setHaloDirty(false);
      })
      .catch(() => {
        toast.error("Failed to save HaloPSA settings");
      });
  }

  const isConfigured = notifSettings.data?.tickettypeId !== null && notifSettings.data?.tickettypeId !== undefined;

  return (
    <GlassCard>
      <div className="flex items-center gap-2 mb-4">
        <Ticket size={18} className="text-sc-primary" />
        <h3 className="text-h3 font-display text-sc-navy">
          HaloPSA Integration
        </h3>
        {!notifSettings.isLoading && (
          <span
            className={`ml-auto text-badge font-medium px-2 py-0.5 rounded-full ${
              isConfigured
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            {isConfigured ? "Configured" : "Not configured"}
          </span>
        )}
      </div>
      {!isConfigured && !notifSettings.isLoading && (
        <div className="p-3 rounded-card bg-amber-900/20 border border-amber-500/30 mb-4">
          <p className="text-body-sm text-amber-400">
            HaloPSA ticket type is not configured. GDAP and PIM notifications
            will not create HaloPSA tickets until a ticket type ID is set.
          </p>
        </div>
      )}
      <div className="space-y-4 max-w-md">
        <div>
          <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
            Ticket Type ID
          </label>
          <input
            type="number"
            min={1}
            value={ticketTypeId}
            onChange={(e) => {
              setTicketTypeId(e.target.value);
              setHaloDirty(true);
            }}
            placeholder="e.g. 26"
            className={`h-9 w-full rounded-input border px-3 text-body-sm text-sc-text bg-sc-input-bg ${
              !ticketTypeValid ? "border-sc-danger" : "border-sc-border"
            }`}
          />
          {!ticketTypeValid && (
            <p className="text-label text-sc-danger mt-1">
              Must be a positive integer
            </p>
          )}
          <p className="text-label text-sc-text-muted mt-1">
            The HaloPSA ticket type ID used for GDAP and PIM notifications.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
              Default Priority
            </label>
            <input
              type="number"
              min={1}
              value={priorityDefault}
              onChange={(e) => {
                setPriorityDefault(e.target.value);
                setHaloDirty(true);
              }}
              className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
            />
            <p className="text-label text-sc-text-muted mt-1">
              INFO / WARNING severity
            </p>
          </div>
          <div>
            <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
              Critical Priority
            </label>
            <input
              type="number"
              min={1}
              value={priorityCritical}
              onChange={(e) => {
                setPriorityCritical(e.target.value);
                setHaloDirty(true);
              }}
              className="h-9 w-full rounded-input border border-sc-border bg-sc-input-bg px-3 text-body-sm text-sc-text"
            />
            <p className="text-label text-sc-text-muted mt-1">
              CRITICAL severity
            </p>
          </div>
        </div>
        {haloDirty && (
          <button
            onClick={handleSaveHalo}
            disabled={!ticketTypeValid || !prioritiesValid || updateTicketType.isPending || updatePriorities.isPending}
            className="flex items-center gap-1 px-4 py-2 rounded-btn text-body-sm font-medium bg-sc-primary text-white hover:bg-sc-primary/90 disabled:opacity-50 transition-colors"
          >
            <Save size={16} />
            {updateTicketType.isPending || updatePriorities.isPending
              ? "Saving..."
              : "Save HaloPSA Settings"}
          </button>
        )}
      </div>
    </GlassCard>
  );
}

/** Read-only collapsible card showing the Teams Specialist default roles. */
function TeamsSpecialistRoleCard() {
  const [expanded, setExpanded] = useState(false);
  const roleCount = TEAMS_SPECIALIST_DEFAULT_ROLES.length;

  return (
    <GlassCard>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <Shield size={18} className="text-sc-primary" />
          <div>
            <span className="text-h3 font-display text-sc-navy">Teams Specialist Roles</span>
            <span className="text-label text-sc-text-muted ml-2">
              {roleCount} default {roleCount === 1 ? "role" : "roles"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-badge text-sc-text-muted">
            {roleCount} {roleCount === 1 ? "role" : "roles"}
          </span>
          {expanded ? (
            <ChevronDown size={16} className="text-sc-text-muted" />
          ) : (
            <ChevronRight size={16} className="text-sc-text-muted" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="mt-4 border-t border-sc-border/50">
          <p className="text-body-sm text-sc-text-muted mt-3 mb-3">
            Entra ID roles assigned to the GDAP-TeamsSpecialist group. These are configured in code and apply to all Teams Specialist relationships.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
            {TEAMS_SPECIALIST_DEFAULT_ROLES.map((roleId) => {
              const displayName =
                ENTRA_ROLE_DISPLAY_NAMES[roleId as keyof typeof ENTRA_ROLE_DISPLAY_NAMES] ??
                roleId;
              return (
                <div
                  key={roleId}
                  className="flex items-center gap-2 px-2 py-1.5 rounded text-body-sm text-sc-text"
                  title={roleId}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sc-primary shrink-0" />
                  {displayName}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </GlassCard>
  );
}

/** Collapsible card showing the Entra roles for a single tier. */
function TierRoleCard({ tier }: { readonly tier: string }) {
  const [expanded, setExpanded] = useState(false);
  const tierConfig = TIER_ROLES[tier as keyof typeof TIER_ROLES];
  const roleCount = tierConfig.roleTemplateIds.length;

  return (
    <div className="rounded-card border border-sc-border bg-sc-surface-hover/30">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-sc-primary/10 text-sc-primary text-badge font-bold shrink-0">
            {tier.split("-")[1]}
          </span>
          <div>
            <span className="text-body-sm font-medium text-sc-text">{tier}</span>
            <span className="text-label text-sc-text-muted ml-2">
              {tierConfig.description}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-badge text-sc-text-muted">
            {roleCount} {roleCount === 1 ? "role" : "roles"}
          </span>
          {expanded ? (
            <ChevronDown size={16} className="text-sc-text-muted" />
          ) : (
            <ChevronRight size={16} className="text-sc-text-muted" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-3 border-t border-sc-border/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 pt-3">
            {tierConfig.roleTemplateIds.map((roleId) => {
              const displayName =
                ENTRA_ROLE_DISPLAY_NAMES[roleId as keyof typeof ENTRA_ROLE_DISPLAY_NAMES] ??
                roleId;
              return (
                <div
                  key={roleId}
                  className="flex items-center gap-2 px-2 py-1.5 rounded text-body-sm text-sc-text"
                  title={roleId}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sc-primary shrink-0" />
                  {displayName}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
